import { createFileRoute, Outlet, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { RequirePermission } from "@/components/portal/RequirePermission";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { spacesApi } from "@/services/api/spaces";
import { ChartSettingsProvider } from "@/components/portal/charts/settings/ChartSettingsProvider";

/**
 * Layout route for /portal/spaces/$spaceId. Holds no UI of its own beyond
 * the permission gate and the chart settings cascade provider (M2-web-a) —
 * mounted here, once, because this is the shallowest point in the tree that
 * has both org and space id, and it wraps both the view (`index`) and
 * `/edit` children, whose widget config editors both need resolved L1/L2/L3.
 * The resource-level `spaces:view` gate lives here so it covers every
 * child; the /edit child adds its own `spaces:edit` gate, and per-space
 * object access is enforced server-side on each API call.
 *
 * `$spaceId` (the route param) is actually the space SLUG, not its UUID —
 * every space route addresses by slug (`spacesApi.getSpace` takes a slug).
 * CP's chart-settings `/resolved?space_id=` requires the real `Space.Id`
 * UUID (plan decision 11) — passing the slug through unchanged 404s on
 * every request. Resolve it here via the same `["space", orgId, spaceId]`
 * query the `index`/`edit` children already run — react-query dedupes the
 * fetch, so this isn't an extra round trip once a child has mounted.
 *
 * `ChartSettingsProvider`'s own `organisationId` prop is deliberately held
 * back until the space query SETTLES (gate-review M3): passing `orgId`
 * straight through would enable the provider's `/resolved` query on first
 * paint, before `space.data.id` exists, firing one request with
 * `space_id` omitted (org-level-only settings) and a second once the real
 * id arrives — a visible L3-less flash plus a wasted request, and the
 * ordering a mocked test could accidentally rely on. Holding `organisationId`
 * back means the provider's query stays disabled until there is a real id
 * to pass (or the space lookup has failed, in which case it stays disabled
 * — the page has bigger problems than a missing chart palette at that
 * point).
 */
export const Route = createFileRoute("/portal/spaces/$spaceId")({
  component: SpaceIdLayout,
});

function SpaceIdLayout() {
  const { spaceId: spaceSlug } = useParams({ from: "/portal/spaces/$spaceId" });
  const { selectedOrganisation } = usePortalData();
  const { token } = useAuth();
  const orgId = selectedOrganisation?.organisationId;

  const space = useQuery({
    queryKey: ["space", orgId, spaceSlug],
    queryFn: () => spacesApi.getSpace(token!, orgId!, spaceSlug),
    enabled: !!token && !!orgId && !!spaceSlug,
  });
  const spaceSettled = space.isSuccess || space.isError;

  return (
    <RequirePermission resource="spaces" action="view">
      <ChartSettingsProvider
        organisationId={spaceSettled ? orgId : undefined}
        spaceId={space.data?.id}
      >
        <Outlet />
      </ChartSettingsProvider>
    </RequirePermission>
  );
}
